import { useEffect, useState } from "react";
import {
  MessagesSquare, Loader2, Send, AlertCircle, CheckCircle2, XCircle,
  HelpCircle, Pencil, FileCheck2, Award, MessageSquare, CornerUpLeft, Dot,
} from "lucide-react";
import type { ChapterEventReview, ChapterEventRequestDetail } from "../api/chapterEventsApi";

/**
 * The review trail on a chapter event request.
 *
 * One component, both sides. The chairman and his reviewer are looking at the
 * same conversation, so rendering it twice is how the two screens end up
 * describing the same exchange differently — the mistake this file exists to
 * prevent, and the same reasoning behind ChapterEventStatusBadge.
 *
 * What differs between the two is only `side`: which entries are drawn as
 * "mine", and what the box at the foot is for. Everything written here is read
 * by both — there is no private lane, by design. A trail the chairman cannot
 * see the whole of is one he has no reason to trust.
 */

type Side = "chapter" | "reviewer";

const ACTIONS: Record<
  string,
  { label: string; icon: typeof MessageSquare; tone: string }
> = {
  raised:               { label: "raised the request",      icon: FileCheck2,    tone: "text-gray-500" },
  amended:              { label: "amended the request",     icon: Pencil,        tone: "text-gray-500" },
  queried:              { label: "raised a query",          icon: HelpCircle,    tone: "text-purple-600" },
  responded:            { label: "answered",                icon: CornerUpLeft,  tone: "text-blue-600" },
  commented:            { label: "commented",               icon: MessageSquare, tone: "text-gray-500" },
  approved:             { label: "approved",                icon: CheckCircle2,  tone: "text-green-600" },
  rejected:             { label: "rejected",                icon: XCircle,       tone: "text-red-600" },
  attendance_submitted: { label: "submitted the register",  icon: FileCheck2,    tone: "text-indigo-600" },
  allocated:            { label: "allocated the CPD",       icon: Award,         tone: "text-green-600" },
};

/** Who the entry came from, in the reader's own terms. */
function actorLabel(entry: ChapterEventReview, side: Side) {
  if (entry.actor_role === "system") return "The portal";
  if (entry.actor) return entry.actor;
  return entry.actor_role === "chapter"
    ? "The chapter"
    : side === "chapter" ? "The reviewer" : "A reviewer";
}

function fmtWhen(iso?: string | null) {
  if (!iso) return "";
  const d = new Date(iso.replace(" ", "T"));
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function safeMsg(e: any): string {
  const errors = e?.data?.errors;
  if (errors) return Object.values(errors).flat().join(" ");
  return e?.data?.message || e?.error || e?.message || "Something went wrong.";
}

// ── one entry ────────────────────────────────────────────────────────────────

function Entry({
  entry,
  side,
  isNew = false,
}: {
  entry: ChapterEventReview;
  side: Side;
  /** New to this reader since they last opened the request. */
  isNew?: boolean;
}) {
  const meta = ACTIONS[entry.action] ?? {
    label: entry.action.replace(/_/g, " "),
    icon: MessageSquare,
    tone: "text-gray-500",
  };
  const Icon = meta.icon;
  const mine = entry.actor_role === side;

  return (
    // Highlighted without changing the padding: the rail and its icon are
    // absolutely positioned against this element, so shifting it moves them off
    // the line. A ring and a tint mark it instead.
    <li className={`relative pl-8 pb-5 last:pb-0 ${isNew ? "bg-amber-50/60 ring-1 ring-amber-200 rounded-lg pr-2 pt-1" : ""}`}>
      {/* The rail. Not drawn past the last entry, so the trail ends rather
          than trailing off into a line going nowhere. */}
      <span className="absolute left-[11px] top-5 bottom-0 w-px bg-gray-200 last:hidden" />
      <span className="absolute left-0 top-0.5 w-[23px] flex justify-center">
        <Icon className={`w-4 h-4 ${meta.tone}`} />
      </span>

      <div className="space-y-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-sm font-bold text-gray-900">
            {mine ? "You" : actorLabel(entry, side)}
            {mine && entry.actor ? ` (${entry.actor})` : ""}
          </span>
          <span className="text-sm text-gray-600">{meta.label}</span>
          {isNew && (
            <span className="text-[10px] font-black uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-400 text-amber-950">
              New
            </span>
          )}
          {entry.points !== null && (
            <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 border">
              {entry.points} pts
            </span>
          )}
          <span className="text-[11px] text-gray-400">{fmtWhen(entry.created_at)}</span>
        </div>

        {!!entry.message && (
          <p
            className={`text-sm whitespace-pre-wrap rounded-xl px-3 py-2 border ${
              entry.actor_role === "chapter"
                ? "bg-blue-50/60 border-blue-100 text-gray-800"
                : "bg-gray-50 border-gray-200 text-gray-800"
            }`}
          >
            {entry.message}
          </p>
        )}
      </div>
    </li>
  );
}

// ── what this reader has not seen ────────────────────────────────────────────

export type TrailUnread = {
  isNew: (entry: ChapterEventReview) => boolean;
  newCount: number;
  firstNewId?: number;
};

/**
 * Which entries are new to this reader, frozen at the moment the page loaded.
 *
 * The freeze is the whole trick. Opening a request marks it read on the server,
 * so every refetch after that — and there is one after every post — comes back
 * saying nothing is new. Captured once, the highlights stay put for as long as
 * the reader is on the page and are gone by their next visit, which is what
 * "unread" is supposed to mean.
 *
 * A HOOK rather than state inside the trail component because the banner at the
 * top of the page and the trail further down have to agree: two copies of this
 * state would be two freezes, and a banner announcing three new entries above a
 * trail highlighting none is worse than neither.
 */
export function useTrailUnread(
  req: ChapterEventRequestDetail | undefined,
  side: Side,
): TrailUnread {
  // `undefined` until the first load resolves — which is what distinguishes
  // "not loaded yet" from "never opened before", the latter arriving as null.
  const [readMark, setReadMark] = useState<string | null | undefined>(undefined);
  const [frozen, setFrozen] = useState(false);

  useEffect(() => {
    if (frozen || !req?.reviews) return;
    setReadMark(req.unread_since ?? null);
    setFrozen(true);
  }, [frozen, req?.reviews, req?.unread_since]);

  const entries = req?.reviews ?? [];

  const isNew = (entry: ChapterEventReview) => {
    if (!frozen) return false;
    // Your own words are never news to you — the server counts it the same way.
    if (entry.actor_role === side) return false;
    if (!entry.created_at) return false;
    // Never opened it before: the whole trail is new.
    if (readMark == null) return true;
    return new Date(entry.created_at.replace(" ", "T")) > new Date(readMark.replace(" ", "T"));
  };

  return {
    isNew,
    newCount: entries.filter(isNew).length,
    firstNewId: entries.find(isNew)?.id,
  };
}

/**
 * The thing that makes an unread trail impossible to walk past.
 *
 * Near the top of the page, above everything the reader came for, because the
 * trail itself sits further down and a conversation nobody scrolls to is a
 * conversation nobody is having.
 */
export function UnreadTrailBanner({ unread }: { unread: TrailUnread }) {
  if (unread.newCount < 1) return null;

  return (
    <a
      href="#review-trail"
      className="flex items-start gap-2 px-3 py-2.5 rounded-lg border-2 border-amber-300 bg-amber-50 text-sm text-amber-900 hover:bg-amber-100 transition-colors"
    >
      <MessagesSquare className="w-4 h-4 mt-0.5 shrink-0" />
      <span>
        <b>
          {unread.newCount} new {unread.newCount === 1 ? "entry" : "entries"} on the review trail
        </b>{" "}
        since you last looked. <span className="underline">Read it →</span>
      </span>
    </a>
  );
}

// ── the trail ────────────────────────────────────────────────────────────────

export default function ChapterEventReviewTrail({
  req,
  side,
  unread,
  onComment,
  onRespond,
  commenting = false,
  responding = false,
}: {
  req: ChapterEventRequestDetail;
  /** Which side of the conversation this screen is. */
  side: Side;
  /** From useTrailUnread, shared with the banner so the two cannot disagree. */
  unread: TrailUnread;
  /** Post a note that decides nothing. Undefined hides the box. */
  onComment?: (message: string) => Promise<void>;
  /**
   * Chairman only, and only while a query is open: answer it and hand the
   * request back. Takes precedence over the comment box, because a chapter
   * sitting on a query should be answering it, not commenting beside it.
   */
  onRespond?: (message: string) => Promise<void>;
  commenting?: boolean;
  responding?: boolean;
}) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");

  const entries = req.reviews ?? [];
  const { isNew, newCount, firstNewId } = unread;
  // Answering wins: it is what moves the request.
  const mode: "respond" | "comment" | null = onRespond ? "respond" : onComment ? "comment" : null;
  const busy = mode === "respond" ? responding : commenting;

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const message = draft.trim();
    if (!message) return;

    setError("");
    try {
      if (mode === "respond") await onRespond!(message);
      else await onComment!(message);
      setDraft("");
    } catch (err) {
      setError(safeMsg(err));
    }
  };

  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden">
      <div className="px-4 sm:px-5 py-3 bg-gray-50 border-b">
        <div className="flex items-center gap-2">
          <MessagesSquare className="w-4 h-4 text-gray-400" />
          <h3 className="font-bold text-gray-900 text-sm">Review trail</h3>
          {entries.length > 0 && (
            <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600">
              {entries.length}
            </span>
          )}
          {newCount > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-400 text-amber-950">
              <Dot className="w-3 h-3" />
              {newCount} new
            </span>
          )}
        </div>
        <p className="text-[11px] text-gray-500 mt-0.5">
          {side === "chapter"
            ? "Everything you and the reviewer have said about this request. The reviewer sees all of it."
            : "Everything said about this request. The chapter chairman sees all of it, including this."}
        </p>
      </div>

      <div className="p-4 sm:p-5">
        {entries.length === 0 ? (
          <p className="text-sm text-gray-400 italic">Nothing on the trail yet.</p>
        ) : (
          <ul className="relative">
            {entries.map((e) => (
              <div key={e.id}>
                {/* The waterline, drawn once above the first thing they have
                    not seen — so a long trail does not have to be scanned for
                    highlights. */}
                {e.id === firstNewId && (
                  <li className="relative flex items-center gap-2 pb-3 list-none">
                    <span className="h-px flex-1 bg-amber-300" />
                    <span className="text-[10px] font-black uppercase tracking-widest text-amber-600">
                      New since you last looked
                    </span>
                    <span className="h-px flex-1 bg-amber-300" />
                  </li>
                )}
                <Entry entry={e} side={side} isNew={isNew(e)} />
              </div>
            ))}
          </ul>
        )}
      </div>

      {mode && (
        <form onSubmit={send} className="px-4 sm:px-5 py-4 border-t bg-gray-50 space-y-2">
          {!!error && (
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {mode === "respond" && (
            <p className="text-xs text-purple-800 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
              <b>Answer the query to send this back.</b> Amend the request or the appendage
              above first if you need to — it stays with you until you answer here.
            </p>
          )}

          <textarea
            rows={3}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={
              mode === "respond"
                ? "Your answer to the reviewer…"
                : "Add a note to the trail…"
            }
            className="w-full border px-3 py-2.5 rounded-lg text-sm outline-none focus:border-blue-500 bg-white"
          />

          <button
            type="submit"
            disabled={busy || !draft.trim()}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-40 ${
              mode === "respond" ? "bg-purple-600 hover:bg-purple-700" : "bg-gray-900 hover:bg-gray-800"
            }`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {mode === "respond" ? "Answer and send back" : "Add to trail"}
          </button>
        </form>
      )}
    </div>
  );
}
